<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import PipelinesAPI from 'custom/dashboard/api/pipelines';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Every change is saved as it is made: adding, renaming (on blur or Enter), reordering by
// dragging and deleting. A failed write reloads the stages, so the list never shows a state
// the server does not have.
const props = defineProps({
  pipelineId: { type: [Number, String], required: true },
  locked: { type: Boolean, default: false },
});

const stages = defineModel({ type: Array, required: true });

const { t } = useI18n();

const reload = async () => {
  const { data } = await PipelinesAPI.getStages(props.pipelineId);
  stages.value = data;
};

const run = async write => {
  try {
    await write();
  } catch {
    useAlert(t('CARDS.PIPELINES_SETTINGS.STAGES.SAVE_ERROR'));
    await reload();
  }
};

const newName = ref('');
const isAdding = ref(false);

const add = async () => {
  const name = newName.value.trim();
  if (!name) return;
  isAdding.value = true;
  await run(async () => {
    const { data } = await PipelinesAPI.createStage(props.pipelineId, {
      name,
    });
    stages.value = [...stages.value, data];
    newName.value = '';
  });
  isAdding.value = false;
};

const rename = (stage, event) => {
  const name = event.target.value.trim();
  if (!name || name === stage.name) {
    event.target.value = stage.name;
    return;
  }
  run(async () => {
    const { data } = await PipelinesAPI.updateStage(
      props.pipelineId,
      stage.id,
      { name }
    );
    Object.assign(stage, data);
  });
};

// vuedraggable has already reordered the list; the server is told the moved stage's new
// position (1-based, acts_as_list) and shifts the others itself.
const move = ({ moved }) => {
  if (!moved) return;
  run(() =>
    PipelinesAPI.updateStage(props.pipelineId, moved.element.id, {
      position: moved.newIndex + 1,
    })
  );
};

const deleteDialog = ref(null);
const pendingDelete = ref(null);

const askDelete = stage => {
  pendingDelete.value = stage;
  deleteDialog.value.open();
};

const confirmDelete = async () => {
  const { id } = pendingDelete.value;
  deleteDialog.value.close();
  await run(async () => {
    await PipelinesAPI.deleteStage(props.pipelineId, id);
    stages.value = stages.value.filter(stage => stage.id !== id);
  });
};
</script>

<template>
  <section class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CARDS.PIPELINES_SETTINGS.STAGES.TITLE') }}
      </h3>
      <p
        v-if="locked"
        class="flex items-center gap-1.5 mb-0 text-sm text-n-slate-11"
        data-testid="stages-locked"
      >
        <Icon icon="i-lucide-lock" class="flex-shrink-0 size-3.5" />
        {{ $t('CARDS.PIPELINES_SETTINGS.STAGES.LOCKED') }}
      </p>
      <p v-else class="mb-0 text-sm text-n-slate-11">
        {{ $t('CARDS.PIPELINES_SETTINGS.STAGES.HINT') }}
      </p>
    </div>

    <Draggable
      v-model="stages"
      item-key="id"
      tag="ol"
      handle="[data-stage-handle]"
      :disabled="locked"
      ghost-class="opacity-40"
      class="flex flex-col gap-2 mb-0 list-none ltr:pl-0 rtl:pr-0"
      @change="move"
    >
      <template #item="{ element: stage, index }">
        <li
          class="flex items-center gap-2 px-2 py-1.5 border rounded-lg group border-n-weak bg-n-solid-1"
          :data-stage-id="stage.id"
        >
          <button
            v-if="!locked"
            type="button"
            data-stage-handle
            class="flex items-center justify-center rounded size-7 text-n-slate-10 hover:text-n-slate-12 cursor-grab active:cursor-grabbing"
            :aria-label="$t('CARDS.PIPELINES_SETTINGS.STAGES.MOVE')"
          >
            <Icon icon="i-lucide-grip-vertical" class="size-4" />
          </button>
          <span
            class="flex items-center justify-center flex-shrink-0 text-xs font-medium rounded-full size-6 bg-n-alpha-2 text-n-slate-11"
          >
            {{ index + 1 }}
          </span>
          <span
            v-if="locked"
            class="flex-1 px-2 py-1 text-sm text-n-slate-12"
            data-testid="stage-name"
          >
            {{ stage.name }}
          </span>
          <input
            v-else
            :value="stage.name"
            class="flex-1 min-w-0 px-2 py-1 mb-0 text-sm bg-transparent border border-transparent rounded-md text-n-slate-12 hover:border-n-weak focus:border-n-brand focus:outline-none"
            data-testid="stage-name"
            @keydown.enter.prevent="$event.target.blur()"
            @blur="rename(stage, $event)"
          />
          <Button
            v-if="!locked"
            v-tooltip.top="$t('CARDS.PIPELINES_SETTINGS.STAGES.DELETE')"
            icon="i-lucide-trash-2"
            size="sm"
            variant="ghost"
            color="ruby"
            class="transition-opacity sm:opacity-0 group-hover:opacity-100 focus:opacity-100"
            :disabled="stages.length <= 1"
            data-testid="stage-delete"
            @click="askDelete(stage)"
          />
        </li>
      </template>
    </Draggable>

    <form
      v-if="!locked"
      class="flex items-center gap-2"
      data-testid="stage-add-form"
      @submit.prevent="add"
    >
      <input
        v-model="newName"
        :placeholder="$t('CARDS.PIPELINES_SETTINGS.STAGES.ADD_PLACEHOLDER')"
        class="flex-1 min-w-0 px-3 py-2 mb-0 text-sm bg-transparent border border-dashed rounded-lg border-n-strong text-n-slate-12 placeholder:text-n-slate-10 focus:border-n-brand focus:border-solid focus:outline-none"
        data-testid="stage-new-name"
      />
      <Button
        type="submit"
        :label="$t('CARDS.PIPELINES_SETTINGS.STAGES.ADD')"
        icon="i-lucide-plus"
        size="sm"
        variant="faded"
        :disabled="!newName.trim()"
        :is-loading="isAdding"
        data-testid="stage-add"
      />
    </form>

    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="
        $t('CARDS.PIPELINES_SETTINGS.STAGES.DELETE_DIALOG.TITLE', {
          name: pendingDelete?.name,
        })
      "
      :description="
        $t('CARDS.PIPELINES_SETTINGS.STAGES.DELETE_DIALOG.DESCRIPTION')
      "
      :confirm-button-label="
        $t('CARDS.PIPELINES_SETTINGS.STAGES.DELETE_DIALOG.CONFIRM')
      "
      data-testid="stage-delete-dialog"
      @confirm="confirmDelete"
    />
  </section>
</template>
